import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createGap,
  deleteGap,
  listGaps,
  updateGap,
  type GapItem,
  type GapWrite,
} from "@/api/project-gaps";
import { diagnosticKeys } from "@/hooks/useDiagnostic";
import { wricefDecisionKeys } from "@/hooks/useWricefDecisions";

/*
  Registro de GAPs do Fit-to-Standard (EF-FUNC-011, RF-03). As duas telas que
  gravam GAP (a aba do nó 04 e a decisão do WRICEF no Architect) usam estes
  hooks pelo formulário comum, e a lista relê depois de cada gravação.
  O Diagnóstico relê junto, porque cada GAP muda o índice de aderência.
*/

export const projectGapKeys = {
  list: (projectId: string) => ["project-gaps", projectId] as const,
};

/** Os GAPs do projeto e os totais da aba. */
export function useProjectGaps(projectId: string | undefined) {
  return useQuery({
    queryKey: projectGapKeys.list(projectId ?? ""),
    queryFn: () => listGaps(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

/**
 * Cadastra (sem `gapId`) ou edita (com `gapId`) um GAP.
 *
 * Mudar ou tirar o objeto WRICEF de um GAP pode tirar o motivo da decisão do
 * objeto anterior, então as decisões releem junto com a lista.
 */
export function useSaveGap(projectId: string | undefined) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ gapId, body }: { gapId?: string; body: GapWrite }): Promise<GapItem> =>
      gapId ? updateGap(projectId!, gapId, body) : createGap(projectId!, body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: projectGapKeys.list(projectId ?? "") });
      client.invalidateQueries({ queryKey: wricefDecisionKeys.all(projectId ?? "") });
      client.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId ?? "") });
    },
  });
}

/** Exclui um GAP; o de WRICEF pode devolver a decisão do objeto para revisão. */
export function useDeleteGap(projectId: string | undefined) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (gapId: string) => deleteGap(projectId!, gapId),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: projectGapKeys.list(projectId ?? "") });
      client.invalidateQueries({ queryKey: wricefDecisionKeys.all(projectId ?? "") });
      client.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId ?? "") });
    },
  });
}
